import { Form, Input, InputNumber, message, Modal, Select } from 'antd'
import { StakingTokenConfigItem, StakingTokenType } from 'Modules'
import { useEffect, useState } from 'react'

import { AppAdmin } from 'api/app-admin'
import { logError } from 'utils/logger'

type ModalConfigMode = 'create' | 'update'

interface ModalConfigProps {
  open: boolean
  mode: ModalConfigMode
  stakingTokenId: string
  poolName: string
  config?: StakingTokenConfigItem | null
  poolOptions?: StakingTokenType[]
  onClose: () => void
  onSuccess: () => void
}

const statusOptions = [
  { value: true, label: 'Active' },
  { value: false, label: 'Inactive' },
]

const ModalConfig = ({
  open,
  mode,
  stakingTokenId,
  poolName,
  config,
  poolOptions,
  onClose,
  onSuccess,
}: ModalConfigProps) => {
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(false)

  const allowPickPool = !!poolOptions && mode === 'create'

  useEffect(() => {
    if (!open) return

    if (mode === 'update' && config) {
      const numericValue = Number(config.value)
      form.setFieldsValue({
        name: config.name,
        value: Number.isFinite(numericValue) ? numericValue : null,
        isActive: config.isActive,
      })
    } else {
      form.resetFields()
      form.setFieldsValue({
        isActive: true,
        ...(allowPickPool ? { stakingTokenId: undefined } : {}),
      })
    }
  }, [open, mode, config, allowPickPool, form])

  const handleOk = async () => {
    try {
      const values = await form.validateFields()

      setLoading(true)
      const stringValue = String(values.value)
      if (mode === 'update' && config) {
        const res = await AppAdmin.updateStakingTokenConfig(config._id, {
          value: stringValue,
          isActive: values.isActive,
        })
        message.success(res?.data?.message || 'Config updated successfully')
      } else {
        const targetPoolId = allowPickPool ? values.stakingTokenId : stakingTokenId
        const res = await AppAdmin.createStakingTokenConfig({
          stakingTokenId: targetPoolId,
          name: values.name,
          value: stringValue,
          isActive: values.isActive,
        })
        message.success(res?.data?.message || 'Config created successfully')
      }

      onSuccess()
      onClose()
    } catch (error: any) {
      if (error?.errorFields) return
      message.error(error?.message || 'Failed to save config')
      logError('Error: ', error)
    } finally {
      setLoading(false)
    }
  }

  const handleCancel = () => {
    if (loading) return
    onClose()
  }

  const selectOptions = (poolOptions || []).map((p) => ({ value: p._id, label: p.name }))

  return (
    <Modal
      title={mode === 'update' ? 'Update Config' : 'Create Config'}
      open={open}
      onOk={handleOk}
      onCancel={handleCancel}
      confirmLoading={loading}
      closable={!loading}
      maskClosable={!loading}
      destroyOnClose
      centered
      width={600}
    >
      <Form form={form} layout="vertical">
        {allowPickPool ? (
          <Form.Item
            label="Pool"
            name="stakingTokenId"
            rules={[{ required: true, message: 'Please choose a pool!' }]}
          >
            <Select
              showSearch
              placeholder="Choose pool"
              optionFilterProp="label"
              options={selectOptions}
            />
          </Form.Item>
        ) : (
          <Form.Item label="Pool">
            <Input value={poolName} disabled />
          </Form.Item>
        )}

        <Form.Item
          label="Name"
          name="name"
          rules={[{ required: true, message: 'Please input config name!' }]}
        >
          <Input placeholder="Enter config name" disabled={mode === 'update'} />
        </Form.Item>

        <Form.Item
          label="Value"
          name="value"
          rules={[{ required: true, message: 'Please input value!' }]}
        >
          <InputNumber placeholder="Enter value" style={{ width: '100%' }} />
        </Form.Item>

        <Form.Item
          label="Status"
          name="isActive"
          rules={[{ required: true, message: 'Please choose status!' }]}
        >
          <Select options={statusOptions} placeholder="Choose status" />
        </Form.Item>
      </Form>
    </Modal>
  )
}

export default ModalConfig
export type { ModalConfigMode }
